import { Button, Card, CardBody, CardHeader, EmptyState, Field, Stack, Text, Textarea } from '@olytic/foundation';
import { ScopeTable } from '@olytic/foundation/cpq';
import { useEffect, useState } from 'react';

import type { CatalogProduct, QuoteBuilder, QuoteScope, ScopeRow } from './quotesApi';

export { parseProducts, productsText } from './scopeText';

const EMPTY: QuoteScope = { summary: '', rows: [] };
const newId = () => Math.random().toString(36).slice(2, 10);

/** The Studio quantities the in-scope themes call for, by product code. */
export function scopeQuantities(scope: QuoteScope): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of scope.rows) {
    if (!row.included) continue;
    for (const p of row.products) out.set(p.code, (out.get(p.code) ?? 0) + p.quantity);
  }
  return out;
}

/**
 * Scope: the application in a paragraph, then each requirement theme with
 * what it covers and the Studio features it needs, as the proposal builder
 * had it. "Build order from scope" sets the order's Studio lines to match.
 * Saved whole as the quote's `scope` document.
 */
export function ScopeTab({
  data,
  catalog,
  busy,
  onSave,
  onBuildOrder,
}: {
  data: QuoteBuilder;
  catalog: CatalogProduct[];
  busy: boolean;
  onSave: (scope: QuoteScope) => void;
  onBuildOrder: (quantities: Map<string, number>) => void;
}): JSX.Element {
  const scope = data.quote.scope ?? EMPTY;
  const [summary, setSummary] = useState(scope.summary);
  useEffect(() => setSummary(scope.summary), [scope.summary]);
  const byCode = new Map(catalog.map((p) => [p.code, p]));
  const save = (rows: ScopeRow[]) => onSave({ summary: scope.summary, rows });
  const patch = (id: string, change: Partial<ScopeRow>) => save(scope.rows.map((r) => (r.id === id ? { ...r, ...change } : r)));

  // Whether the order's Studio lines already match the scope.
  const wanted = scopeQuantities(scope);
  const onOrder = new Map<string, number>();
  for (const l of data.lines) if (l.product_code && l.family === 'studio' && !l.parent_line_id) onOrder.set(l.product_code, (onOrder.get(l.product_code) ?? 0) + l.quantity);
  const matches = wanted.size === onOrder.size && [...wanted].every(([code, q]) => onOrder.get(code) === q);

  return (
    <Stack>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Text tone="muted">Summarize the application, then list each requirement theme and the Studio features it needs.</Text>
        <div className="flex items-center gap-3">
          <Text tone="muted">{wanted.size === 0 ? 'Add features to a theme to build the order from it.' : matches ? 'The order matches this scope.' : 'The order differs from this scope.'}</Text>
          <Button variant={matches ? 'neutral' : 'brand'} size="sm" disabled={busy || matches || wanted.size === 0} onClick={() => onBuildOrder(wanted)}>
            Build order from scope
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader eyebrow="Application" title={data.quote.name} />
        <CardBody>
          <Field label="Summary">
            <Textarea
              rows={4}
              value={summary}
              disabled={busy}
              onChange={(event) => setSummary(event.target.value)}
              onBlur={() => {
                if (summary !== scope.summary) onSave({ summary, rows: scope.rows });
              }}
            />
          </Field>
        </CardBody>
      </Card>
      {scope.rows.length ? (
        <ScopeTable
          disabled={busy}
          rows={scope.rows.map((r) => ({
            id: r.id,
            included: r.included,
            theme: r.theme,
            covers: r.covers,
            items: r.products.map((p) => ({ key: p.code, label: byCode.get(p.code)?.name ?? p.code, quantity: p.quantity })),
          }))}
          addOptions={catalog.filter((p) => p.family === 'studio').map((p) => ({ value: p.code, label: p.name }))}
          onChange={patch}
          onAddItem={(id, code) => {
            const row = scope.rows.find((r) => r.id === id);
            if (!row) return;
            const has = row.products.find((p) => p.code === code);
            patch(id, { products: has ? row.products.map((p) => (p.code === code ? { ...p, quantity: p.quantity + 1 } : p)) : [...row.products, { code, quantity: 1 }] });
          }}
          onRemoveItem={(id, code) => {
            const row = scope.rows.find((r) => r.id === id);
            if (row) patch(id, { products: row.products.filter((p) => p.code !== code) });
          }}
          onRemove={(id) => save(scope.rows.filter((r) => r.id !== id))}
        />
      ) : (
        <Card>
          <CardBody>
            <EmptyState title="No themes yet" description="Group the work into themes: what each covers and the Studio features that deliver it." />
          </CardBody>
        </Card>
      )}
      <div>
        <Button
          variant="neutral"
          size="sm"
          disabled={busy}
          onClick={() => save([...scope.rows, { id: newId(), included: true, theme: 'New theme', covers: '', stories: '', products: [] }])}
        >
          Add theme
        </Button>
      </div>
    </Stack>
  );
}
